import React from 'react';

import { createSearchParams } from 'react-router-dom';

import { Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { toInclusiveEndDate } from '~/components/Content/SearchAnalytics/SearchBigQueryDateRangePicker';

import type { AnalyticsDateRange } from '~/services/AnalyticsService';

import SectionedList from './SectionedList';
import type { GroupedSearchStats } from './groupSearchStats';

type GroupedSearchTableProps = {
  groups: Array<GroupedSearchStats>;
  dateRange: AnalyticsDateRange;
};

function buildReproduceSearchHref(searchId: string, dateRange: AnalyticsDateRange) {
  const inclusiveEndDate = toInclusiveEndDate(dateRange.endDate);

  return `/search/reproduce-search?${createSearchParams({
    searchId,
    startDate: dateRange.startDate,
    endDate: inclusiveEndDate,
  }).toString()}`;
}

const contentSizedColumnSx = {
  width: '1%',
  whiteSpace: 'nowrap',
} as const;

export default function GroupedSearchTable({ groups, dateRange }: GroupedSearchTableProps) {
  return (
    <TableContainer>
      <Table size="small" sx={{ width: '100%', tableLayout: 'auto', '& th, & td': { verticalAlign: 'top' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={contentSizedColumnSx}>Count</TableCell>
            <TableCell sx={contentSizedColumnSx}>Natural language query</TableCell>
            <TableCell sx={contentSizedColumnSx}>Extracted</TableCell>
            <TableCell sx={{ width: '100%' }}>Prefix matches</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <TableRow key={group.naturalLanguageQuery}>
              <TableCell sx={contentSizedColumnSx}>{group.count.toLocaleString()}</TableCell>
              <TableCell sx={contentSizedColumnSx}>
                <Link
                  href={buildReproduceSearchHref(group.firstSearchId, dateRange)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {group.naturalLanguageQuery}
                </Link>
              </TableCell>
              <TableCell sx={contentSizedColumnSx}>
                <SectionedList
                  sections={[
                    { label: 'Destinations', items: group.destinationIds },
                    { label: 'Landmarks', items: group.landmarkIds },
                    { label: 'Properties', items: group.propertyIds },
                  ]}
                />
              </TableCell>
              <TableCell sx={{ width: '100%' }}>
                <SectionedList
                  sections={[
                    { label: 'Places', items: group.prefixMatchedGeometryNames },
                    { label: 'Properties', items: group.prefixMatchedOfferNames },
                  ]}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
